import * as THREE from "three";
import {BaseLineMaterial, CustomMaterials} from './CustomMaterials'
import AssetUtility from './AssetUtility';
import ProjectSaver from './ProjectSaver';
import {assert} from './utilities/ErrorHandling';
import VisibilityManager from './VisibilityManager';
import Engine from './Engine';
import MathUtils from './utilities/MathUtils';
import EffectorEventInvocation from './timeline/effectors/EffectorEventInvocation';
import DefaultSceneManager from './DefaultSceneManager';
import {APIField, RegisterClassWithAPI} from "./api/Decorators";
import { getUuidv4 } from "./utilities/Misc";
import SceneObjectUtils from "./utilities/SceneObjectUtils";
import AssetsLibraryUtilities from '../components/filebrowser/AssetsLibraryUtilities';
import {VisibilityModes} from "../interfaces/enums";
import {
	AlphaModes,
	CustomEventTypes,
	defaultTriggerObject,
	MaterialTypes,
	RenderModes,
	SaveModificationTypes,
	ValueTypes
} from '../globals';
import SetMaterialPropertyAction from '../actions/SetMaterialPropertyAction';
import MaterialPropertyTypes from '../components/materials/MaterialPropertyTypes';
import store, {StoreActions} from '../state/store';
import StandbyOverlay from "../components/layout/StandbyOverlay";
import { MaterialPreviewRenderer } from "../components/materials/MaterialPreviewRenderer";

interface MaterialReduxProperty {
	property: string;
	value: any;
	shouldSubmit: boolean;
}

interface Materialinstance {
	materialAssetGuid: string;
	object: THREE.Object3D;
	material: THREE.Material;
	videoElementClone: HTMLVideoElement | null;
	videoTimelineOverride: false;
	isVideoPlaying: () => boolean;
	assignVideoElementClone: () => void;
	onFadeInEffect: (factor: number) => void;
	onColorChangeEffect: (factor: number) => void;
	loadVideoElementClone(video: THREE.VideoTexture, resolve: (value?: unknown) => void, reject: (reason?: any) => void) : void;
}

interface VideoInstanceLoadQueueItem {
	material: Materialinstance;
	video: THREE.VideoTexture;
	resolveFunc: (value?: unknown) => void;
	rejectFunc: (reason?: any) => void;
}

interface MaterialSaveObject {
	guid: string;
	type: MaterialTypes;
	name: string;
	alphaMode: AlphaModes;
	alphaTest: number;
	opacity: number;
	metalness: number;
	roughness: number;
	color: string;
	emissive: string;
	lineColor: string;
	lineThickness: number;
	map?: string;
	aoMap?: string;
	normalMap?: string;
	emissiveMap?: string;
	metallicRoughnessMap?: string;
	video?: string;
}

export interface MaterialsSaveData {
	materials: MaterialSaveObject[];
	textures: string[];
	videos: string[];
}

export interface NewMaterialParams {
	type: MaterialTypes;
	isDefaultMaterial?: boolean;
	name?: string;
}

type CopyMaterialAssetParams = {
	sourceMaterial: MaterialAsset;
};
type CopyMaterialParams = {
	sourceMaterial: THREE.MeshStandardMaterial;
	type: MaterialTypes;
};

/**
 * save format which we only keep around for backwards compatibility
 */
type TextureAssetIdentifier = {
	assetId: string;
};

type OldSavedMaterialParams = {
	guid: string;
	name: string;
	alphaMode: AlphaModes;
	alphaCutoff?: number;
	emissiveFactor?: [number, number, number];
	occlusionTexture?: TextureAssetIdentifier;
	normalTexture?: TextureAssetIdentifier;
	emissiveTexture?: TextureAssetIdentifier;
	extras: {
		type: MaterialTypes;
		guid: string;
		srgbColors: boolean;
		asset: string;
	};
	pbrMetallicRoughness: Partial<{
		baseColorFactor: [number, number, number, number];
		metallicFactor: number;
		roughnessFactor: number;
		baseColorTexture: TextureAssetIdentifier;
		metallicRoughnessTexture: TextureAssetIdentifier;
	}>;
};

type MaterialAssetParams = MaterialSaveObject | NewMaterialParams | CopyMaterialAssetParams | CopyMaterialParams | OldSavedMaterialParams;
@RegisterClassWithAPI("MaterialAsset",
	{params: "{type: MaterialTypes, isDefaultMaterial: boolean, name: string}"},
	"Creates a new MaterialAsset and adds it to the library")
export default class MaterialAsset
{
	private _guid = "";
	@APIField({ type: "string" })
	get guid(): string { return this._guid; }

	private _name = "";
	@APIField({ type: "string" })
	get name(): string { return this._name; }
	set name(value: string) { this._name = value; }

	private _internalMaterial: THREE.Material | null = null;
	get internalMaterial(): THREE.Material | null { return this._internalMaterial; }

	private _type: MaterialTypes = MaterialTypes.Standard;
	@APIField({ type: "MaterialTypes" })
	get type() { return this._type; }

	private color: THREE.Color = new THREE.Color(1, 1, 1);
	private emissive: THREE.Color = new THREE.Color(0, 0, 0);
	private _lineColor: THREE.Color = new THREE.Color(1, 1, 1);
	get lineColor(): THREE.Color { return this._lineColor; }
	set lineColor(value: THREE.Color) { this._lineColor = value; }
	private map: THREE.Texture | null = null;
	private aoMap: THREE.Texture | null = null;
	private normalMap: THREE.Texture | null = null;
	private emissiveMap: THREE.Texture | null = null;
	private metallicRoughnessMap: THREE.Texture | null = null;
	private video: THREE.VideoTexture | null = null;
	get videoDuration(): number { return this.video ? this.video.source.data.duration : 0; }
	private _alphaMode: AlphaModes = AlphaModes.Opaque;
	private alphaTest = 0.5;
	private opacity =  1.0;
	private _metalness =  1.0;
	get metalness(): number { return this._metalness; }
	set metalness(value: number) { this._metalness = value; }
	private roughness =  1.0;
	private _lineThickness = 3;
	get lineThickness(): number { return this._lineThickness; }
	set lineThickness(value: number) { this._lineThickness = value; }
	private hiddenFromView = false;
	private _assignedObjects: THREE.Mesh[] = [];
	get assignedObjects(): THREE.Mesh[] { return this._assignedObjects; }

	private customMaterialObjects: Set<THREE.Object3D | EffectorEventInvocation> = new Set<THREE.Object3D | EffectorEventInvocation>();
	private unsubscribeFromStore: any;
	private existingMaterialState: any = null;
	private _forcePackage = false;
	get forcePackage(): boolean { return this._forcePackage; }
	set forcePackage(value: boolean) { this._forcePackage = value; }

	private  initial_type = this.type;
	private  initial_name = this.name;
	private  initial_color = this.color;
	private  initial_map = this.map;
	private  initial_aoMap = this.aoMap;
	private  initial_alphaMode = this._alphaMode;
	private  initial_alphaTest = this.alphaTest;
	private  initial_opacity = this.opacity;
	private  initial_normalMap = this.normalMap;
	private  initial_emissive = this.emissive;
	private  initial_emissiveMap = this.emissiveMap;
	private  initial_metalness = this.metalness;
	private  initial_metallicRoughnessMap = this.metallicRoughnessMap;
	private  initial_roughness = this.roughness;
	private  initial_video = this.video;
	private  initial_lineColor = this.lineColor;
	private  initial_lineThickness = this.lineThickness;

	private static _materialAssetBank: Map<string, MaterialAsset> = new Map<string, MaterialAsset>();
	private static textureBank: Map<string, THREE.Texture> = new Map<string, THREE.Texture>();
	private static videoElementBank: Map<string, HTMLVideoElement> = new Map<string, HTMLVideoElement>();
	private static videoTextureBank = new Map();
	private static objectGuidToMaterialAsset = new Map<string, MaterialAsset>();
	private static objectGuidToMaterialInstance: Map<string, Materialinstance> = new Map<string, Materialinstance>();
	private static materialInstanceAssetToObjects: Map<string, THREE.Mesh[]> = new Map<string, THREE.Mesh[]>();
	private static _prePackageVideoMaterialVideo = new Map();
	private static prePackageExternalMaterial = new Map();
	private static namelessMaterialCounter = 1;
	private static videoInstancesLoadPromises: Promise<any>[] = [];
	private static videoInstancesLoadQueue: VideoInstanceLoadQueueItem[] = [];
	private static videoInstanceCurrentlyLoading = false;

	private static _defaultMaterialGuid = 'defaultMat';
	private static _defaultMaterial : MaterialAsset | null = null;
	static get materialAssetBank(): Map<string, MaterialAsset> { return MaterialAsset._materialAssetBank; }
	static get defaultMaterialGuid(): string { return MaterialAsset._defaultMaterialGuid; }
	static get prePackageVideoMaterialVideo() { return MaterialAsset._prePackageVideoMaterialVideo; }
	static get defaultMaterial(): MaterialAsset
	{
		if(!MaterialAsset._defaultMaterial)
		{
			MaterialAsset._defaultMaterial = new MaterialAsset({ type: MaterialTypes.Standard, isDefaultMaterial : true});
		}
		return MaterialAsset._defaultMaterial;
	}

	constructor(params: MaterialAssetParams)
	{
		if ('guid' in params && params.guid && !('extras' in params)) //materials loaded from new save format
		{
			this._guid = params.guid;
			this._type = params.type;
			this.name = params.name;
			this._alphaMode = params.alphaMode;
			this.alphaTest = params.alphaTest;
			this.opacity = params.opacity;
			this.metalness = params.metalness;
			this.roughness = params.roughness;
			this.color = new THREE.Color().setHex(parseInt(params.color), THREE.SRGBColorSpace);
			this.emissive = new THREE.Color().setHex(parseInt(params.emissive), THREE.SRGBColorSpace);
			this.map = MaterialAsset.getTexture(params.map!);
			this.aoMap = MaterialAsset.getTexture(params.aoMap!);
			this.normalMap = MaterialAsset.getTexture(params.normalMap!);
			this.emissiveMap = MaterialAsset.getTexture(params.emissiveMap!);
			this.metallicRoughnessMap = MaterialAsset.getTexture(params.metallicRoughnessMap!);
			if(params.video) this.video = MaterialAsset.getVideoTexture(params.video);
			if(params.lineColor) this.lineColor.setHex(parseInt(params.lineColor), THREE.SRGBColorSpace);
			if(params.lineThickness) this._lineThickness = params.lineThickness;
		}
		else if ('sourceMaterial' in params && params.sourceMaterial)
		{
			if (params.sourceMaterial instanceof MaterialAsset) //copy pasting material assets
			{
				this._guid = getUuidv4();
				this._type = params.sourceMaterial.type;
				this.name = "New Material" + MaterialAsset.namelessMaterialCounter++;
				this.map = params.sourceMaterial.map;
				this.aoMap = params.sourceMaterial.aoMap;
				this._alphaMode = params.sourceMaterial._alphaMode;
				this.alphaTest = params.sourceMaterial.alphaTest;
				this.opacity = params.sourceMaterial.opacity;
				this.normalMap = params.sourceMaterial.normalMap;
				this.emissiveMap = params.sourceMaterial.emissiveMap;
				this.metalness = params.sourceMaterial.metalness;
				this.metallicRoughnessMap = params.sourceMaterial.metallicRoughnessMap;
				this.roughness = params.sourceMaterial.roughness;
				this.video = params.sourceMaterial.video;
				this._lineThickness = params.sourceMaterial.lineThickness

				let colorSRGB = store.getState().materialsModule.materials[params.sourceMaterial.guid].color.value;
				colorSRGB = colorSRGB.substr(1);
				this.color.setHex(parseInt('0x' + colorSRGB, 16), THREE.SRGBColorSpace);

				let emissiveSRGB = store.getState().materialsModule.materials[params.sourceMaterial.guid].emissive.value;
				emissiveSRGB = emissiveSRGB.substr(1);
				this.emissive.setHex(parseInt('0x' + emissiveSRGB, 16), THREE.SRGBColorSpace);

				let lineColorSRGB = store.getState().materialsModule.materials[params.sourceMaterial.guid].lineColor.value;
				lineColorSRGB = lineColorSRGB.substr(1);
				this.lineColor.setHex(parseInt('0x' + lineColorSRGB, 16), THREE.SRGBColorSpace);
			}
			else if ( params.sourceMaterial instanceof THREE.MeshStandardMaterial)
			{
				const sourceMaterial = params.sourceMaterial as THREE.MeshStandardMaterial;
				this._guid = sourceMaterial.userData.guid;
				this._type = ('type' in params && params.type) || MaterialTypes.Standard;
				this.name = sourceMaterial.name ? sourceMaterial.name : "New Material" + MaterialAsset.namelessMaterialCounter++;
				this.color = sourceMaterial.color; //TODO is sourcematerial color in linear space?
				this.map = sourceMaterial.map;
				this.aoMap = sourceMaterial.aoMap;
				this.normalMap = sourceMaterial.normalMap;

				this.alphaTest = sourceMaterial.alphaTest;
				this.opacity = sourceMaterial.opacity;
				if (sourceMaterial.transparent && (this.opacity < 1 || this.map))
				{//not all transparent materials should be set as blend. if there is no possible source for alpha we force it to opaque instead
					this._alphaMode = AlphaModes.Blend;
				}
				else if(sourceMaterial.userData.isMaskAlphaMode)
				{
					this._alphaMode = AlphaModes.Mask;
				}
				else
				{
					this._alphaMode = AlphaModes.Opaque;
				}

				this.emissive = sourceMaterial.emissive;
				this.emissiveMap = sourceMaterial.emissiveMap;
				this.metalness = sourceMaterial.metalness;
				this.metallicRoughnessMap = sourceMaterial.metalnessMap;
				this.roughness = sourceMaterial.roughness;
				this.video = null;

				const materialAsset = this;
				Object.keys(materialAsset).forEach(function (key, index)
				{
					if (materialAsset[key] && materialAsset[key].isTexture)
					{
						const textureAssetId = materialAsset[key].userData.assetId;
						if (textureAssetId === undefined)
						{
							//the fbx2gltf converter sometimes creates invalid textures.. we don't want those..
							let texture = materialAsset[key];
							materialAsset[key] = null;
							texture.dispose();
							texture = null;
						}
						else if (!MaterialAsset.textureBank.has(textureAssetId))
						{
							MaterialAsset.textureBank.set(textureAssetId, materialAsset[key]);
						}
					}
				});
			}
		}
		else if ('extras' in params && params.extras && params.extras.type) //materials loaded from old save format
		{
			const inputColorSpace = params.extras.srgbColors ? THREE.SRGBColorSpace : THREE.LinearSRGBColorSpace;
			this._guid = params.extras.guid;
			this._type = params.extras.type;
			this.name = params.name;
			assert(params.pbrMetallicRoughness)

			if (params.pbrMetallicRoughness.baseColorFactor)
			{
				this.color = new THREE.Color().setRGB(
					params.pbrMetallicRoughness.baseColorFactor[0],
					params.pbrMetallicRoughness.baseColorFactor[1],
					params.pbrMetallicRoughness.baseColorFactor[2],
					inputColorSpace);
				this.opacity = Number(params.pbrMetallicRoughness.baseColorFactor[3]);
			}
			else
			{
				this.color = new THREE.Color(1, 1, 1);
				this.opacity = 1;
			}
			this.metalness = Number(params.pbrMetallicRoughness.metallicFactor ? params.pbrMetallicRoughness.metallicFactor : 1);
			this.roughness = Number(params.pbrMetallicRoughness.roughnessFactor ? params.pbrMetallicRoughness.roughnessFactor : 1);
			this._alphaMode = params.alphaMode;

			this.alphaTest = params.alphaCutoff ? Number(params.alphaCutoff) : 0.5;
			this.emissive = params.emissiveFactor ? new THREE.Color().setRGB(
				params.emissiveFactor[0],
				params.emissiveFactor[1],
				params.emissiveFactor[2],
				inputColorSpace)
				: new THREE.Color(0, 0, 0);

			this.map = params.pbrMetallicRoughness.baseColorTexture ? MaterialAsset.getTexture(params.pbrMetallicRoughness.baseColorTexture.assetId) : null;
			this.metallicRoughnessMap = params.pbrMetallicRoughness.metallicRoughnessTexture ? MaterialAsset.getTexture(params.pbrMetallicRoughness.metallicRoughnessTexture.assetId) : null;
			this.aoMap = params.occlusionTexture ? MaterialAsset.getTexture(params.occlusionTexture.assetId) : null;
			this.normalMap = params.normalTexture ? MaterialAsset.getTexture(params.normalTexture.assetId) : null;
			this.emissiveMap = params.emissiveTexture ? MaterialAsset.getTexture(params.emissiveTexture.assetId) : null;
			this.video = null;
			if (params.extras.type === MaterialTypes.Video && params.extras.asset) this.video = MaterialAsset.getVideoTexture(params.extras.asset);
		}
		else //new material with default values
		{
			const isDefaultMaterial = ('isDefaultMaterial' in params && params.isDefaultMaterial);
			this._guid = isDefaultMaterial ? MaterialAsset.defaultMaterialGuid : getUuidv4();
			this.name = isDefaultMaterial ? "DefaultMaterial" : ('name' in params && params.name) ? params.name : "New Material" + MaterialAsset.namelessMaterialCounter++;
			this._type = ('type' in params && params.type) || MaterialTypes.Standard;
			this.color = new THREE.Color(1, 1, 1);
			this.map = null;
			this.aoMap = null;
			this._alphaMode = AlphaModes.Opaque;
			this.alphaTest = 0.5;
			this.opacity = 1;
			this.normalMap = null;
			this.emissive = new THREE.Color(0, 0, 0);
			this.emissiveMap = null;
			this.metalness = 1;
			this.metallicRoughnessMap = null;
			this.roughness = 1;
			this.video = null;
		}

		//convert deprecated 'diffuse' type to standard
		if(this._type.toString() === "diffuse")
		{
			this._type = MaterialTypes.Standard;
			this.metalness = 0;
			this.roughness = 1;
		}

		MaterialAsset.materialAssetBank.set(this.guid, this);

		if('isDefaultMaterial' in params && params.isDefaultMaterial)
		{
			this.setType(this.type);
			return;
		}

		this.existingMaterialState = null;
		this.unsubscribeFromStore = store.subscribe(() =>
		{
			const state = store.getState();
			if (state.materialsModule.materials && state.materialsModule.materials[this.guid])
			{
				const materialState = state.materialsModule.materials[this.guid];
				if (!this.existingMaterialState)
				{
					this.existingMaterialState = Object.assign({}, materialState);
				}
				const changedProperties: MaterialReduxProperty[] = [];
				for (const property of Object.keys(materialState))
				{
					if(!this.existingMaterialState[property] || !materialState[property]) continue;

					if (this.existingMaterialState[property].value !== materialState[property].value)
					{
						changedProperties.push({
							property: property,
							value: materialState[property].value,
							shouldSubmit: materialState[property].shouldSubmit
						})
					}
				}
				//cache existing material state
				this.existingMaterialState = Object.assign({}, materialState);

				//raise events
				for (const changedProperty of changedProperties)
				{
					this.handleChange(changedProperty.property, changedProperty.value, changedProperty.shouldSubmit)
				}
			}

			//check if a material was submitted
			if (state.materialsModule.submitted && state.materialsModule.submitted.id === this.guid)
			{
				const submitted = Object.assign({}, state.materialsModule.submitted);
				store.dispatch(StoreActions.material.submitMaterial({ id: null, property: null })); //clear submitted material
				this.handleSubmit(submitted.property!, submitted.value);
			}
		})

		//dispatch to add this material
		store.dispatch(StoreActions.material.addMaterial({
			id: this.guid,
			hidden: this.hiddenFromView,
			value: {
				type: {
					type: MaterialPropertyTypes.TYPE,
					value: this.type,
					visible: true
				},
				name: {
					type: MaterialPropertyTypes.TEXT,
					value: this.name,
					visible: true
				},
				color: {
					type: MaterialPropertyTypes.COLOR,
					value: '#' + this.color.getHexString(THREE.SRGBColorSpace),
					visible: true
				},
				map: {
					type: MaterialPropertyTypes.TEXTURE,
					value: this.map ? this.map.userData.assetId : "",
					visible: true
				},
				alphaMode: {
					type: MaterialPropertyTypes.OPTIONS,
					value: this._alphaMode,
					options: [
						AlphaModes.Opaque,
						AlphaModes.Blend,
						AlphaModes.Mask
					],
					visible: true
				},
				alpha: {
					type: MaterialPropertyTypes.SLIDER,
					value: this.opacity,
					visible: this._alphaMode !== AlphaModes.Opaque
				},
				alphaCutoff: {
					type: MaterialPropertyTypes.SLIDER,
					value: this.alphaTest,
					visible: this._alphaMode === AlphaModes.Mask
				},
				normalMap: {
					type: MaterialPropertyTypes.TEXTURE,
					value: this.normalMap ? this.normalMap.userData.assetId : "",
					visible: true
				},
				metallicRoughnessMap: {
					type: MaterialPropertyTypes.TEXTURE,
					value: this.metallicRoughnessMap ? this.metallicRoughnessMap.userData.assetId : "",
					visible: true
				},
				metalness: {
					type: MaterialPropertyTypes.SLIDER,
					value: this.metalness,
					visible: true
				},
				roughness: {
					type: MaterialPropertyTypes.SLIDER,
					value: this.roughness,
					visible: true
				},
				emissive: {
					type: MaterialPropertyTypes.COLOR,
					value: '#' + this.emissive.getHexString(THREE.SRGBColorSpace),
					visible: true
				},
				emissiveMap: {
					type: MaterialPropertyTypes.TEXTURE,
					value: this.emissiveMap ? this.emissiveMap.userData.assetId : "",
					visible: true
				},
				aoMap: {
					type: MaterialPropertyTypes.TEXTURE,
					value: this.aoMap ? this.aoMap.userData.assetId : "",
					visible: true
				},
				video: {
					type: MaterialPropertyTypes.VIDEO,
					value: this.video ? this.video.userData.assetId : "",
					visible: true
				},
				lineColor: {
					type: MaterialPropertyTypes.COLOR,
					value: '#' + this.lineColor.getHexString(THREE.SRGBColorSpace),
					visible: true
				},
				lineThickness: {
					type: MaterialPropertyTypes.NUMBER,
					value: this.lineThickness,
					visible: true,
					min: 0,
					step: 0.5,
				},
			}
		}))

		
		this.setType(this.type);
		this.setInitialEditValues();
		this.renderPreview();
	}

	async renderPreview() : Promise<Blob | undefined>
	{
		if (process.env.JEST_WORKER_ID) return;
		if (this.internalMaterial) return MaterialPreviewRenderer.renderPreview(this.guid, this.internalMaterial);
		return undefined;
	}

	get alphaMode(): AlphaModes { return this._alphaMode; }

	setInitialEditValues() : void
	{
		this.initial_type = this.type;
		this.initial_name = this.name;
		this.initial_color = this.color;
		this.initial_map = this.map;
		this.initial_aoMap = this.aoMap;
		this.initial_alphaMode = this._alphaMode;
		this.initial_alphaTest = this.alphaTest;
		this.initial_opacity = this.opacity;
		this.initial_normalMap = this.normalMap;
		this.initial_emissive = this.emissive;
		this.initial_emissiveMap = this.emissiveMap;
		this.initial_metalness = this.metalness;
		this.initial_metallicRoughnessMap = this.metallicRoughnessMap;
		this.initial_roughness = this.roughness;
		this.initial_video = this.video;
		this.initial_lineColor = this.lineColor;
		this.initial_lineThickness = this.lineThickness;
	}

	getMapsInUse() : THREE.Texture[]
	{
		switch(this.type)
		{
			case MaterialTypes.DepthMask:
			case MaterialTypes.Line:
				return [];
			case MaterialTypes.DiffuseUnlit:
				return this.map ? [this.map] : [];
			case MaterialTypes.Standard:
				const textures: THREE.Texture[] = [];
				if (this.map) textures.push(this.map);
				if (this.aoMap) textures.push(this.aoMap);
				if (this.normalMap) textures.push(this.normalMap);
				if (this.emissiveMap) textures.push(this.emissiveMap);
				if (this.metallicRoughnessMap) textures.push(this.metallicRoughnessMap);
				break;
			case MaterialTypes.Video:
				return this.video ? [this.video] : [];
			default:
				assert(false, "unidentified material type: " + this.type);
		}

		return [];
	}

	handleChange(propertyName: string, value: any, shouldSubmit = true) : void
	{
		switch (propertyName)
		{
			case 'type':
				this.setType(value);
				break;
			case 'color':
			case 'emissive':
			case 'lineColor':
				this.setProperty(propertyName, new THREE.Color(value));
				break;
			case 'map':
			case 'emissiveMap':
			case 'normalMap':
			case 'aoMap':
			case 'metallicRoughnessMap':
				if (value)
				{
					const texture = MaterialAsset.getTexture(value);
					if (texture)
					{
						this.setProperty(propertyName, texture);
						if (shouldSubmit) this.handleSubmit(propertyName, value);
					}
					else
					{
						MaterialAsset.loadTexture(value)?.then(texture =>
						{
							this.setProperty(propertyName, texture);
							AssetsLibraryUtilities.refresh();
							if (shouldSubmit) this.handleSubmit(propertyName, value);
						});
					}
				}
				else
				{
					this.setProperty(propertyName, null);
					if (shouldSubmit) this.handleSubmit(propertyName, value);
				}
				break;
			case 'alphaMode':
				this.setAlphaMode(value);
				this.setProperty('depthWrite', this._alphaMode !== AlphaModes.Blend);
				break;
			case 'alpha':
				this.setProperty('opacity', value);
				break;
			case 'alphaCutoff':
				this.setProperty('alphaTest', value);
				break;
			case 'video':
				if (value)
				{
					MaterialAsset.loadVideoTexture(value)?.then(texture =>
					{
						this.setProperty(propertyName, texture);
						AssetsLibraryUtilities.refresh();
						if (shouldSubmit) this.handleSubmit(propertyName, value);
					});
				}
				else
				{
					this.setProperty(propertyName, null);
					if (shouldSubmit) this.handleSubmit(propertyName, value);
				}
				break;
			default:
				this.setProperty(propertyName, value);
				break;
		}
		MaterialPreviewRenderer.clearPreview(this.guid);
	}

	handleSubmit(propertyName: string, value: any) : void
	{
		let oldValue;
		let internalPropertyName = propertyName;
		switch (propertyName)
		{
			case 'color':
			case 'emissive':
				oldValue = "#" + this["initial_" + internalPropertyName].getHexString();
				break;
			case 'map':
			case 'emissiveMap':
			case 'normalMap':
			case 'aoMap':
			case 'metallicRoughnessMap':
			case 'video':
				oldValue = this["initial_" + internalPropertyName] ? this["initial_" + internalPropertyName].userData.assetId : "";
				break;
			case 'alpha':
				internalPropertyName = 'opacity';
				oldValue = this.initial_opacity;
				break;
			case 'alphaCutoff':
				internalPropertyName = 'alphaTest';
				oldValue = this.initial_alphaTest;
				break;
			default:
				oldValue = this["initial_" + internalPropertyName];
				break;
		}
		new SetMaterialPropertyAction(this, propertyName, value, oldValue);
		this["initial_" + internalPropertyName] = this[internalPropertyName];
	}

	setAlphaMode(mode: AlphaModes) : void
	{
		switch (mode)
		{
			case AlphaModes.Opaque:
				this.setProperty('transparent', false, false);
				this.setProperty('opacity', 1, false);
				this.setProperty('alphaTest', 0, false);
				if(this.guid !== MaterialAsset.defaultMaterialGuid) store.dispatch(StoreActions.material.setMaterialPropertyVisiblity({
					id: this.guid,
					property: 'alpha',
					value: false
				}));
				if(this.guid !== MaterialAsset.defaultMaterialGuid) store.dispatch(StoreActions.material.setMaterialPropertyVisiblity({
					id: this.guid,
					property: 'alphaCutoff',
					value: false
				}));
				break;
			case AlphaModes.Blend:
				this.setProperty('transparent', true, false);
				this.setProperty('opacity', this.opacity);
				this.setProperty('alphaTest', 0, false);
				if(this.guid !== MaterialAsset.defaultMaterialGuid) store.dispatch(StoreActions.material.setMaterialPropertyVisiblity({
					id: this.guid,
					property: 'alpha',
					value: true
				}));
				if(this.guid !== MaterialAsset.defaultMaterialGuid) store.dispatch(StoreActions.material.setMaterialPropertyVisiblity({
					id: this.guid,
					property: 'alphaCutoff',
					value: false
				}));
				break;
			case AlphaModes.Mask:
				this.setProperty('transparent', false, false);
				this.setProperty('opacity', this.opacity);
				this.setProperty('alphaTest', this.alphaTest);
				if(this.guid !== MaterialAsset.defaultMaterialGuid) store.dispatch(StoreActions.material.setMaterialPropertyVisiblity({
					id: this.guid,
					property: 'alpha',
					value: true
				}));
				if(this.guid !== MaterialAsset.defaultMaterialGuid) store.dispatch(StoreActions.material.setMaterialPropertyVisiblity({
					id: this.guid,
					property: 'alphaCutoff',
					value: true
				}));
				break;
		}
		this._alphaMode = mode;
	}

	setProperty(propertyName: string, value: any, overrideValue = true)	: void
	{
		assert(this.internalMaterial);

		const isVideoMap = propertyName === 'video';

		if (propertyName === 'metallicRoughnessMap')
		{
			this.setProperty('metalnessMap', value, false);
			this.setProperty('roughnessMap', value, false);
			this.metallicRoughnessMap = value;
			return;
		}
		if ((propertyName === 'map' || propertyName === 'emissiveMap') && value !== null && value.colorSpace !== THREE.SRGBColorSpace)
		{
			value.colorSpace = THREE.SRGBColorSpace;
			value.needsUpdate = true;
		}

		if (overrideValue) this[propertyName] = value;
		if (isVideoMap) propertyName = 'map'; //we wanna set the material.map to the video texture, but maintain this.map to the actual color map

		this.internalMaterial![propertyName] = value;

		const materialUpdate = propertyName.includes('map') || propertyName.includes('Map') || propertyName === 'alpha' || propertyName === 'alphaTest';
		this.internalMaterial!.needsUpdate = materialUpdate;

		if(MaterialAsset.materialInstanceAssetToObjects.has(this.guid))
		{
			const objects = MaterialAsset.materialInstanceAssetToObjects.get(this.guid);
			if(objects)
			{
				for(const object of objects)
				{
					const instance = MaterialAsset.getTimelineMaterialInstance(object);
					assert(instance);

					instance!.material[propertyName] = value;
					instance!.material.needsUpdate = materialUpdate;
					if(object.userData.fadeInFactor !== undefined && object.userData.fadeInFactor < 1.0)
					{
						SceneObjectUtils.applyFadeInEffect(object);
					}
					if(object.userData.overrideColorFactor !== undefined && object.userData.overrideColorFactor > 0)
					{
						SceneObjectUtils.applyColorChangeEffect(object);
					}

					if (isVideoMap)
					{
						instance!.assignVideoElementClone();
					}

				}
			}
		}
	}

	setType(type: MaterialTypes) : void
	{
		this._type = type;

		if(this.internalMaterial) this.internalMaterial.dispose();

		switch (this.type)
		{
			case MaterialTypes.DiffuseUnlit:
				this._internalMaterial = CustomMaterials.getDiffuseUnlitMaterial();
				break;
			case MaterialTypes.DepthMask:
				this._internalMaterial = CustomMaterials.getDepthMaskMaterial();
				break;
			case MaterialTypes.Standard:
				this._internalMaterial = CustomMaterials.getStandardMaterial();
				break;
			case MaterialTypes.Video:
				this._internalMaterial = CustomMaterials.getVideoMaterial();
				break;
			case MaterialTypes.Line:
				this._internalMaterial = CustomMaterials.getBaseLineMaterial();
				break;
			default:
				assert("Unkown material type: " + this.type);
				return;
		}
		assert(this.internalMaterial);

		this.internalMaterial!.userData.guid = this.guid;

		switch (this.type)
		{
			case MaterialTypes.DiffuseUnlit:
				this.setProperty('depthWrite', this._alphaMode !== AlphaModes.Blend);
				this.setProperty('name', this.name);
				this.setProperty('color', this.color);
				this.setProperty('map', this.map);
				this.setProperty('alphaTest', this.alphaTest);
				this.setProperty('opacity', this.opacity);
				this.setAlphaMode(this._alphaMode);
				break;
			case MaterialTypes.Standard:
				this.setProperty('depthWrite', this._alphaMode !== AlphaModes.Blend);
				this.setProperty('name', this.name);
				this.setProperty('color', this.color);
				this.setProperty('map', this.map);
				this.setProperty('aoMap', this.aoMap);
				this.setProperty('normalMap', this.normalMap);
				this.setProperty('alphaTest', this.alphaTest);
				this.setProperty('opacity', this.opacity);
				this.setProperty('emissiveMap', this.emissiveMap);
				this.setProperty('emissive', this.emissive);
				this.setProperty('metalness', this.metalness);
				this.setProperty('metalnessMap', this.metallicRoughnessMap);
				this.setProperty('roughnessMap', this.metallicRoughnessMap);
				this.setProperty('roughness', this.roughness);
				this.setAlphaMode(this._alphaMode);
				break;
			case MaterialTypes.Video:
				this.setProperty('depthWrite', true);
				this.setProperty('name', this.name);
				this.setProperty('color', this.color);
				this.setProperty('video', this.video);
				break;
			case MaterialTypes.DepthMask:
				this.setProperty('depthWrite', true);
				this.setProperty('name', this.name);
				break;
			case MaterialTypes.Line:
				this.setProperty('depthWrite', true);
				this.setProperty('name', this.name);
				this.setProperty('lineColor', this.lineColor);
				this.setProperty('lineThickness', this.lineThickness);
				break;
			default:
				console.error("Unkown material type: " + this.type);
				break;
		}

		for (const object of this.assignedObjects)
		{
			if(!MaterialAsset.getTimelineMaterialInstance(object))
			{
				object.material = this.internalMaterial!;
			}
		}
		const objectsWithMaterialInstance = MaterialAsset.materialInstanceAssetToObjects.get(this.guid);
		if(objectsWithMaterialInstance)
		{
			for(const object of objectsWithMaterialInstance)
			{
				MaterialAsset.updateMaterialTimelineInstance(object, this.internalMaterial!);
				if (this.type === MaterialTypes.Video)
				{
					const instance = MaterialAsset.getTimelineMaterialInstance(object);
					instance?.assignVideoElementClone();
				}
			}
		}

		if((this.assignedObjects.length || objectsWithMaterialInstance?.length) && Engine.getInstance()?.getSelectionRoot())
		{
			VisibilityManager.setObjectsVisibilityMode([Engine.getInstance().getSelectionRoot()], [Engine.getInstance().getSelectionRoot().userData.visibility[VisibilityManager.getRenderMode()]]);
		}
	}

	isHiddenFromView() : boolean
	{
		return this.hiddenFromView;
	}

	setHiddenInAssetView(isHidden: boolean) : void
	{
		if(this.hiddenFromView === isHidden) return;

		if(this.hiddenFromView && !isHidden)
		{
			store.dispatch(StoreActions.material.unhideMaterial({ id: this.guid }));
		}
		else
		{
			store.dispatch(StoreActions.material.hideMaterial({ id: this.guid }));
		}

		this.hiddenFromView = isHidden;
	}

	isAssignedToAnyObject() : boolean
	{
		for (let i = this.assignedObjects.length - 1; i >= 0; i--) //reading in reverse since none-cached objects are more likely to be at the end of the list
		{
			const object = this.assignedObjects[i];
			if (!object.userData.isCachedAssetObject && !object.userData.removedFromScene)
			{
				return true;
			}
		}

		for(const object of this.customMaterialObjects)
		{
			if(object instanceof THREE.Object3D)
			{
				if(object.userData.visibility?.customDeviceMaterial === this.guid || object.userData.visibility?.customStandaloneMaterial === this.guid)
				{
					return true;
				}

				if(object.userData.triggers)
				{
					for(const trigger of object.userData.triggers)
					{
						if(trigger.customMaterial === this.guid)
						{
							return true;
						}
					}
				}
			}
			else if(object instanceof EffectorEventInvocation)
			{
				if(object.customMaterial === this.guid)
				{
					return true;
				}
			}
		}

		return false;
	}

	static assignMaterialAssetToObject(materialAsset: MaterialAsset, object: THREE.Mesh, updateVisibility = false) : void
	{
		const previouslyAssignedMaterialAsset = MaterialAsset.objectGuidToMaterialAsset.get(object.userData.guid);
		if(previouslyAssignedMaterialAsset === materialAsset) return;

		materialAsset.assignedObjects.push(object);
		MaterialAsset.objectGuidToMaterialAsset.set(object.userData.guid, materialAsset);

		//on project load, before objects are assigned their saved guid,
		//they are assigned a material asset based on the source model asset's material
		//that means that MaterialAsset.objectGuidToMaterialAsset will not return any material asset, as it was registered before the object's current guid was assigned.
		//we fetch the material asset directly from the material instead.
		const previousMaterialAsset = previouslyAssignedMaterialAsset || (object.material && MaterialAsset.getMaterialAssetByGuid((object.material as THREE.Material).userData.guid));
		if (previousMaterialAsset && previousMaterialAsset !== materialAsset)
		{
			const index = previousMaterialAsset.assignedObjects.indexOf(object);
			if(index >= 0)
			{
				previousMaterialAsset.assignedObjects.splice(index, 1);
			}

			const timelineInstanceMaterial = MaterialAsset.getTimelineMaterialInstance(object);
			if (timelineInstanceMaterial)
			{
				if(timelineInstanceMaterial.materialAssetGuid === previousMaterialAsset.guid)
				{
					MaterialAsset.updateMaterialTimelineInstance(object, materialAsset.internalMaterial as THREE.Material);
				}

				if (materialAsset.type === MaterialTypes.Video)
				{
					timelineInstanceMaterial.assignVideoElementClone();
				}
				return;
			}
		}

		object.material = materialAsset.internalMaterial as THREE.Material;
		if(updateVisibility && object.userData.visibility)	VisibilityManager.setObjectsVisibilityMode([object], [object.userData.visibility[VisibilityManager.getRenderMode()]]);

	}

	static getTimelineMaterialInstance(object: THREE.Object3D) : Materialinstance | undefined
	{
		return MaterialAsset.objectGuidToMaterialInstance.get(object.userData.guid);
	}

	static getOrCreateMaterialTimelineInstance(object: THREE.Mesh, material: THREE.Material) : Materialinstance
	{
		assert(material.userData.guid);
		let instance = MaterialAsset.getTimelineMaterialInstance(object);
		if (!instance)
		{
			const internalMaterialClone = material.clone();
			internalMaterialClone.userData.guid = material.userData.guid;
			instance =
			{
				materialAssetGuid: material.userData.guid,
				object: object,
				material: internalMaterialClone,
				videoElementClone: null,
				videoTimelineOverride: false,

				isVideoPlaying() : boolean
				{
					return !!(this.videoElementClone &&
						this.videoElementClone.currentTime > 0 &&
						!this.videoElementClone.paused &&
						!this.videoElementClone.ended &&
						this.videoElementClone.readyState > 2);
				},

				onFadeInEffect(factor : number)  : void
				{
					const materialAsset = MaterialAsset.getMaterialAssetByGuid(this.materialAssetGuid);
					assert(materialAsset);

					if(materialAsset!.type === MaterialTypes.Line)
					{
						(this.material as BaseLineMaterial).alpha = factor;
					}
					else
					{
						switch(materialAsset!._alphaMode)
						{
							case AlphaModes.Opaque:
								this.material.opacity = factor;
								this.material.transparent = true;
								this.material.depthWrite = (factor === 1);
								this.material.needsUpdate = true;
								break;
							case AlphaModes.Blend:
								this.material.opacity = factor * materialAsset!.opacity;
								this.material.transparent = true;
								this.material.depthWrite = false;
								this.material.needsUpdate = true;
								break;
							case AlphaModes.Mask:
								this.material.opacity = factor * materialAsset!.opacity;
								this.material.transparent = false;
								this.material.depthWrite = true;
								this.material.needsUpdate = true;
								break;
						}
					}

				},

				onColorChangeEffect(factor: number)  : void
				{
					const materialAsset = MaterialAsset.getMaterialAssetByGuid(this.materialAssetGuid);
					assert(materialAsset);

					if(materialAsset!.type === MaterialTypes.Line)
					{
						(this.material as BaseLineMaterial).lineColor = MathUtils.lerpValue(materialAsset!.lineColor, this.object.userData.overrideColor, factor, ValueTypes.Color);
					}
					else
					{
						if ('color' in (this.material as any)) (this.material as any).color = MathUtils.lerpValue(materialAsset!.color, this.object.userData.overrideColor, factor, ValueTypes.Color);
					}
				},

				assignVideoElementClone() : void
				{
					const materialAsset = MaterialAsset.getMaterialAssetByGuid(this.materialAssetGuid);
					assert(materialAsset);
					if(!materialAsset!.video) return;
					const videoElement = MaterialAsset.getVideoElement(materialAsset!.video.userData.assetId!);
					if(videoElement && this.videoElementClone?.src === videoElement.src) return;

					MaterialAsset.videoInstancesLoadPromises.push(new Promise<any>((resolve, reject) =>
					{
						assert(materialAsset!.video);

						if(MaterialAsset.videoInstanceCurrentlyLoading || MaterialAsset.videoInstancesLoadQueue.length)
						{
							MaterialAsset.videoInstancesLoadQueue.push({material: this, video: materialAsset!.video!, resolveFunc: resolve, rejectFunc: reject});
						}
						else
						{

							this.loadVideoElementClone(materialAsset!.video!, resolve, reject);
						}
					}));
				},

				loadVideoElementClone(video: THREE.VideoTexture, resolve, reject) : void
				{
					MaterialAsset.videoInstanceCurrentlyLoading = true;
					StandbyOverlay.startStandbyMode("Loading Content");
					if (this.videoElementClone) this.videoElementClone.remove();
					const videoElement = MaterialAsset.getVideoElement(video.userData.assetId!);
					assert(videoElement);
					this.videoElementClone = videoElement!.cloneNode(true) as HTMLVideoElement;

					const onVideoLoaded = () =>
					{
						assert(this.videoElementClone);

						this.videoElementClone!.removeEventListener("seeked", onVideoLoaded);
						this.videoElementClone!.removeEventListener("error", onVideoError);
						this.videoElementClone!.currentTime = 0.0;

						const texture = new THREE.VideoTexture(this.videoElementClone!);
						texture.userData = {
							assetId: video.userData.assetId
						};
						texture.format = THREE.RGBAFormat;
						texture.colorSpace = THREE.SRGBColorSpace;
						texture.flipY = false;
						if ('map' in (this.material as any)) (this.material as any).map = texture;
						this.material.needsUpdate = true;

						StandbyOverlay.stopStandbyMode();
						handleVideoQueue();
						resolve();
					}
					const onVideoError = () =>
					{
						assert(this.videoElementClone);

						this.videoElementClone!.removeEventListener("seeked", onVideoLoaded);
						this.videoElementClone!.removeEventListener("error", onVideoError);
						this.videoElementClone!.remove();
						this.videoElementClone = null;

						handleVideoQueue();
						StandbyOverlay.stopStandbyMode();
						reject();
					}

					const handleVideoQueue = () =>
					{
						MaterialAsset.videoInstanceCurrentlyLoading = false;
						if(MaterialAsset.videoInstancesLoadQueue.length)
						{
							const firstInQueue = MaterialAsset.videoInstancesLoadQueue.shift()!;
							firstInQueue.material.loadVideoElementClone(firstInQueue.video, firstInQueue.resolveFunc, firstInQueue.rejectFunc);
						}
					}

					//chrome can't handle more than 6 video dom elements having their play() function called simulationsly
					//to force video load, we instead set the currentTime and use the seeked event to track when it loaded
					if(this.videoElementClone)
					{
						this.videoElementClone.addEventListener("seeked", onVideoLoaded);
						this.videoElementClone.addEventListener("error", onVideoError);
						this.videoElementClone.currentTime = 0.1;
					}
				}
			}

			MaterialAsset.objectGuidToMaterialInstance.set(object.userData.guid, instance);
			if(!MaterialAsset.materialInstanceAssetToObjects.has(material.userData.guid))
			{
				MaterialAsset.materialInstanceAssetToObjects.set(material.userData.guid, []);
			}
			MaterialAsset.materialInstanceAssetToObjects.get(material.userData.guid)?.push(object);

			const refreshVisibility = (object.userData.visibility && !(object.material as THREE.Material).userData.guid);
			object.material = instance.material;
			if(refreshVisibility) VisibilityManager.setObjectsVisibilityMode([object], [object.userData.visibility[VisibilityManager.getRenderMode()]]);

		}
		return instance;
	}

	static updateMaterialTimelineInstance(object: THREE.Mesh, material : THREE.Material)
	{
		const materialInstance = MaterialAsset.getTimelineMaterialInstance(object);
		if(materialInstance)
		{
			const materialAsset = MaterialAsset.getMaterialAssetByGuid(material.userData.guid);
			assert(materialAsset);

			const skipRecreatingMaterial = materialInstance.videoElementClone &&
				materialInstance.materialAssetGuid === materialAsset?.guid &&
				materialAsset.type === MaterialTypes.Video && 'map' in materialInstance.material &&
				materialAsset.video?.userData?.assetId === (materialInstance.material.map as THREE.Texture)?.userData?.assetId;

			if(!skipRecreatingMaterial)
			{
				assert(materialAsset!.internalMaterial);
				assert(materialAsset?.guid);

				MaterialAsset.disposeMaterialTimelineInstance(object);

				//avoids multiple guards
				if(materialAsset?.guid)
				{
					materialInstance.material = materialAsset.internalMaterial!.clone();
					materialInstance.material.userData.guid = materialAsset.guid;
					materialInstance.materialAssetGuid = materialAsset.guid;
					object.material = materialInstance.material;

					if(!MaterialAsset.materialInstanceAssetToObjects.has(materialAsset.guid))
					{
						MaterialAsset.materialInstanceAssetToObjects.set(materialAsset.guid, []);
					}
					MaterialAsset.materialInstanceAssetToObjects.get(materialAsset.guid)?.push(object);
					MaterialAsset.objectGuidToMaterialInstance.set(object.userData.guid, materialInstance);

					materialInstance.assignVideoElementClone();
				}
			}

			if(object.userData.fadeInFactor !== undefined)
			{
				SceneObjectUtils.applyFadeInEffect(materialInstance.object);
			}
			if(materialInstance.object.userData.overrideColorFactor !== undefined)
			{
				SceneObjectUtils.applyColorChangeEffect(materialInstance.object);
			}
		}
	}

	static disposeMaterialTimelineInstance(object: THREE.Mesh) : void
	{
		const instance = MaterialAsset.getTimelineMaterialInstance(object);
		if(instance)
		{
			const bank = MaterialAsset.materialInstanceAssetToObjects.get(instance.materialAssetGuid);
			if(bank) bank.splice(bank.indexOf(object), 1);
			if(instance.videoElementClone) instance.videoElementClone.remove();

			if ('map' in instance.material && (instance.material as any).map !== null && (instance.material as any).map instanceof THREE.VideoTexture) (instance.material as any).map.dispose();
			instance.material.dispose();
			MaterialAsset.objectGuidToMaterialInstance.delete(object.userData.guid);
		}
	}

	static getMaterialAssets() : IterableIterator<MaterialAsset>
	{
		return MaterialAsset.materialAssetBank.values();
	}

	static getMaterialAssetByObjectGuid(guid: string)
	{
		return MaterialAsset.objectGuidToMaterialAsset.get(guid);
	}

	static getMaterialAssetByGuid(guid: string) : MaterialAsset | undefined
	{
		if(guid === MaterialAsset.defaultMaterialGuid) return MaterialAsset.defaultMaterial;
		return MaterialAsset.materialAssetBank.get(guid);
	}

	static preProjectPackage() : void
	{
		for (const materialAsset of MaterialAsset.materialAssetBank.values())
		{
			assert(materialAsset.internalMaterial);

			if (materialAsset.type === MaterialTypes.Video)
			{
				//we don't want the .map property of video materials to be exported as an actual gltf texture
				MaterialAsset.prePackageVideoMaterialVideo.set(materialAsset.guid, materialAsset.video);
				if('map' in  materialAsset.internalMaterial!) (materialAsset.internalMaterial as any).map = null;
			}

			for (const object of materialAsset.assignedObjects)
			{
				//objects may, for whatever reason (visibility mode, timeline changes..) not be using their material asset's material at this time
				//we must assign them the material asset's material in order for it to package properly, and later restore it
				if (object.material !== materialAsset.internalMaterial)
				{
					if((object.material as THREE.Material)?.userData?.guid)
					{
						const usedAsset = MaterialAsset.getMaterialAssetByGuid((object.material as THREE.Material).userData.guid);
						if(usedAsset) usedAsset._forcePackage = true;
					}
					MaterialAsset.prePackageExternalMaterial.set(object, object.material);
				}

				object.material = materialAsset.internalMaterial!;

				if(object.userData.visibility[RenderModes.Standalone] === VisibilityModes.Custom)
				{
					const customStandaloneMaterialAsset = MaterialAsset.getMaterialAssetByGuid(object.userData.visibility.customStandaloneMaterial);
					if(customStandaloneMaterialAsset) customStandaloneMaterialAsset._forcePackage = true;
				}
				if(object.userData.visibility[RenderModes.Device] === VisibilityModes.Custom)
				{
					const customDeviceMaterialAsset = MaterialAsset.getMaterialAssetByGuid(object.userData.visibility.customDeviceMaterial);
					if(customDeviceMaterialAsset) customDeviceMaterialAsset._forcePackage = true;
				}
			}
		}
	}

	static postProjectPackage(): void
	{
		for (const materialAsset of MaterialAsset.materialAssetBank.values())
		{
			assert( materialAsset.internalMaterial);

			materialAsset._forcePackage = false;

			if (materialAsset.type === MaterialTypes.Video)
			{
				if('map' in  materialAsset.internalMaterial!) (materialAsset.internalMaterial as any).map = MaterialAsset.prePackageVideoMaterialVideo.get(materialAsset.guid);
			}

			for (const object of materialAsset.assignedObjects)
			{
				if (MaterialAsset.prePackageExternalMaterial.has(object))
				{
					object.material = MaterialAsset.prePackageExternalMaterial.get(object);
				}
				else
				{
					object.material = materialAsset.internalMaterial!;
				}
			}
		}

		MaterialAsset.prePackageExternalMaterial.clear();
		MaterialAsset.prePackageVideoMaterialVideo.clear();
	}

	public static getPacakgedVideoMaterialAsset(materialAssetId: string): string
	{
		const video = MaterialAsset.prePackageVideoMaterialVideo.get(materialAssetId);
		if(video)
		{
			return video.userData.assetId;
		}
		return "";
	}

	static loadVideoTexture(videoAssetId: string, retryIteration = 0) : Promise<THREE.VideoTexture>
	{
		return new Promise(function (resolve, reject)
		{
			function retry()
			{
				clearTimeout(timeOut);
				if(retryIteration >= 2)
				{
					reject(new Error('Video Texture loading timed out'))
				}
				else
				{
					console.log('Retrying video texture load', videoAssetId);
					MaterialAsset.loadVideoTexture(videoAssetId, retryIteration + 1)?.then(resolve).catch(reject);
				}
			}
			const timeOut = setTimeout(retry, 30000);

			if(!MaterialAsset.videoTextureBank.has(videoAssetId))
			{
				StandbyOverlay.startStandbyMode("Loading Content");
				const videoElement = document.createElement('video');
				videoElement.crossOrigin = "anonymous";
				videoElement.src = AssetUtility.getRawAssetURL(videoAssetId);
				videoElement.volume = 0;

				function onVideoLoaded()
				{
					videoElement.removeEventListener("seeked", onVideoLoaded);
					videoElement.removeEventListener("error", onVideoError);

					videoElement.pause();
					videoElement.currentTime = 0;

					const texture = new THREE.VideoTexture(videoElement);
					texture.userData = {
						assetId: videoAssetId
					};

					texture.format = THREE.RGBAFormat;
					texture.colorSpace = THREE.SRGBColorSpace;
					texture.flipY = false;

					MaterialAsset.videoTextureBank.set(videoAssetId, texture);
					MaterialAsset.videoElementBank.set(videoAssetId, videoElement);
					StandbyOverlay.stopStandbyMode();
					clearTimeout(timeOut);
					resolve(texture);
				}

				function onVideoError()
				{
					videoElement.removeEventListener("seeked", onVideoLoaded);
					videoElement.removeEventListener("error", onVideoError);
					videoElement.removeAttribute('src');
					videoElement.load();
					StandbyOverlay.stopStandbyMode();
					console.log("Could not load video: " + videoAssetId);
					clearTimeout(timeOut);
					reject();
				}

				//chrome can't handle more than 6 video dom elements having their play() function called simulationsly
				//to force video load, we instead set the currentTime and use the seeked event to track when it loaded
				videoElement.addEventListener("seeked", onVideoLoaded);
				videoElement.addEventListener("error", onVideoError);
				videoElement.currentTime = 0.1;
			}
			else
			{
				clearTimeout(timeOut);
				resolve(MaterialAsset.videoTextureBank.get(videoAssetId));
			}
		});
	}

	static loadTexture(imageAssetId: string) : Promise<THREE.Texture>
	{
		if(MaterialAsset.textureBank.has(imageAssetId))
		{
			//console.warn("Requested to load a texture that has already been loaded!", imageAssetId);
			return Promise.resolve(MaterialAsset.textureBank.get(imageAssetId)!);
		}

		return new Promise(function (resolve, reject)
		{
			StandbyOverlay.startStandbyMode("Loading Content");
			const textureURL = AssetUtility.getRawAssetURL(imageAssetId);
			new THREE.TextureLoader().load(textureURL, function (texture)
			{
				texture.flipY = false;
				texture.magFilter = THREE.LinearFilter;
				texture.minFilter = THREE.LinearMipmapLinearFilter;
				texture.wrapS = THREE.RepeatWrapping;
				texture.wrapT = THREE.RepeatWrapping;

				texture.userData =
				{
					assetId: imageAssetId
				};
				MaterialAsset.textureBank.set(imageAssetId, texture);
				StandbyOverlay.stopStandbyMode();
				resolve(texture);
			},
			function (xhr)
			{
				console.log("loading texture " + Math.round(xhr.loaded / xhr.total * 100.0) + "%");
			},
			function (error)
			{
				console.warn('Could not load texture ' + imageAssetId);
				StandbyOverlay.stopStandbyMode();
				reject();
			}
			);
		})
	}

	static getVideoTexture(videoAssetId: string) : THREE.VideoTexture | null
	{
		return MaterialAsset.videoTextureBank.get(videoAssetId) || null;
	}

	static getVideoElement(assetId: string) : HTMLVideoElement | null
	{
		return MaterialAsset.videoElementBank.get(assetId) || null;
	}

	static async waitForVideoLoadPromises()
	{
		await Promise.all(this.videoInstancesLoadPromises);
		this.videoInstancesLoadPromises.length = 0;
	}

	static getTexture(assetId: string) : THREE.Texture | null
	{
		return MaterialAsset.textureBank.get(assetId) || null;
	}

	static deleteMaterial(assetId: string, raiseUIUpdateEvents = true) : void
	{
		assert(assetId !== MaterialAsset.defaultMaterialGuid);
		const materialAsset = MaterialAsset.materialAssetBank.get(assetId);
		assert(materialAsset);
		assert(materialAsset?.internalMaterial);

		if (materialAsset!.unsubscribeFromStore) materialAsset!.unsubscribeFromStore();
		store.dispatch(StoreActions.material.removeMaterial({ id: materialAsset!.guid }));

		for (let i = materialAsset!.assignedObjects.length - 1; i >= 0; i--)
		{
			const object = materialAsset!.assignedObjects[i];
			MaterialAsset.assignMaterialAssetToObject(MaterialAsset.defaultMaterial, object, true);
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Material);
		}

		MaterialAsset.replaceObjectCustomMaterialReferences(materialAsset!, undefined, true);

		materialAsset!.customMaterialObjects.clear();
		materialAsset!.assignedObjects.length = 0;
		MaterialAsset.materialAssetBank.delete(assetId);

		materialAsset!.internalMaterial!.dispose();
		materialAsset!._internalMaterial = null;

		if(MaterialAsset.materialInstanceAssetToObjects.has(materialAsset!.guid))
		{
			const objects = MaterialAsset.materialInstanceAssetToObjects.get(materialAsset!.guid);
			if(objects)
			{
				for(const object of objects)
				{
					MaterialAsset.disposeMaterialTimelineInstance(object);
					MaterialAsset.getOrCreateMaterialTimelineInstance(object, MaterialAsset.defaultMaterial.internalMaterial as THREE.Material);
				}
			}
		}

		if(raiseUIUpdateEvents)
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.updateMaterialComponent));
			document.dispatchEvent(new CustomEvent(CustomEventTypes.updatePropertiesComponent));
			document.dispatchEvent(new CustomEvent(CustomEventTypes.updateTriggerComponent));
			AssetsLibraryUtilities.refresh();
		}
	}

	static deleteUnassignedMaterials() : void
	{
		for (const materialAsset of MaterialAsset.materialAssetBank.values())
		{
			if(materialAsset.guid === MaterialAsset.defaultMaterialGuid) continue;

			const usedBySceneObject = materialAsset.isAssignedToAnyObject();
			if(usedBySceneObject) continue;

			let usedByCachedObjects = false;

			const removedFromSceneObjects: THREE.Mesh[] = [];
			for (let i = materialAsset.assignedObjects.length - 1; i >= 0; i--)
			{
				const object = materialAsset.assignedObjects[i];
				if(object.userData.isCachedAssetObject)
				{
					usedByCachedObjects = true;
				}
				else if(object.userData.removedFromScene)
				{
					removedFromSceneObjects.push(object);
				}
			}

			for(const object of removedFromSceneObjects)
			{
				MaterialAsset.assignMaterialAssetToObject(MaterialAsset.defaultMaterial, object, true);
				ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Material);
			}

			//only delete the actual asset if it is actually unused.
			//if cached objects are using it we want to keep the actual asset and only hide it from the asset library view
			if(usedByCachedObjects)
			{
				materialAsset.setHiddenInAssetView(true);
			}
			else
			{
				MaterialAsset.deleteMaterial(materialAsset.guid, false);
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.updateMaterialComponent));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updatePropertiesComponent));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updateTriggerComponent));
		AssetsLibraryUtilities.refresh();
	}

	static clearData() : void
	{
		for (const materialAsset of MaterialAsset.materialAssetBank.values())
		{
			if (materialAsset.unsubscribeFromStore) materialAsset.unsubscribeFromStore();

			if(MaterialAsset.materialInstanceAssetToObjects.has(materialAsset.guid))
			{
				const objects = MaterialAsset.materialInstanceAssetToObjects.get(materialAsset.guid);
				if(objects)
				{
					for(const object of objects)
					{
						MaterialAsset.disposeMaterialTimelineInstance(object);
					}
				}
			}

			materialAsset.internalMaterial?.dispose();
			materialAsset.assignedObjects.length = 0;
			materialAsset.customMaterialObjects.clear();
		}

		for(const texture of MaterialAsset.textureBank.values()) texture.dispose();
		for(const texture of MaterialAsset.videoTextureBank.values()) texture.dispose();
		for(const videoElement of MaterialAsset.videoElementBank.values()) videoElement.remove();

		MaterialAsset.textureBank.clear();
		MaterialAsset.videoElementBank.clear();
		MaterialAsset.videoTextureBank.clear();
		MaterialAsset.materialAssetBank.clear();
		MaterialAsset.objectGuidToMaterialAsset.clear();
		MaterialAsset.namelessMaterialCounter = 1;
		MaterialAsset.videoInstancesLoadPromises.length = 0;
		MaterialAsset.videoInstancesLoadQueue.length = 0;
		MaterialAsset.videoInstanceCurrentlyLoading = false;
		MaterialAsset.materialInstanceAssetToObjects.clear();
		MaterialAsset.objectGuidToMaterialInstance.clear();
		MaterialAsset._defaultMaterial = null;
		store.dispatch(StoreActions.material.clearMaterials());
	}

	static disassociateMaterialAsset(object: THREE.Mesh) : void
	{
		const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(object.userData.guid);
		if(materialAsset)
		{
			MaterialAsset.objectGuidToMaterialAsset.delete(object.userData.guid);
			const index = materialAsset.assignedObjects.indexOf(object);
			materialAsset.assignedObjects.splice(index, 1);
		}
	}

	static onObjectGuidReplaced(oldGuid: string, newGuid: string) : void
	{
		const materialAsset = MaterialAsset.getMaterialAssetByObjectGuid(oldGuid);
		if(materialAsset)
		{
			MaterialAsset.objectGuidToMaterialAsset.delete(oldGuid);
			MaterialAsset.objectGuidToMaterialAsset.set(newGuid, materialAsset);

			if(MaterialAsset.objectGuidToMaterialInstance.has(oldGuid))
			{
				const instance = MaterialAsset.objectGuidToMaterialInstance.get(oldGuid);
				MaterialAsset.objectGuidToMaterialInstance.delete(oldGuid);
				MaterialAsset.objectGuidToMaterialInstance.set(newGuid, instance!);
			}
		}
	}

	static overrideObjectGuidMaterialAssetReference(objectGuid: string, materialAsset: MaterialAsset | undefined = undefined) : void
	{
		if(MaterialAsset.objectGuidToMaterialAsset.has(objectGuid))
		{
			if(materialAsset)
			{
				MaterialAsset.objectGuidToMaterialAsset.set(objectGuid, materialAsset);
			}
			else
			{
				MaterialAsset.objectGuidToMaterialAsset.delete(objectGuid);
			}
		}
	}

	static overrideMaterialInstanceObjectReference(oldObject: THREE.Object3D, newObject: THREE.Object3D) : void
	{
		const instance = MaterialAsset.getTimelineMaterialInstance(oldObject);
		if(instance)
		{
			MaterialAsset.objectGuidToMaterialInstance.delete(oldObject.userData.guid);
			MaterialAsset.objectGuidToMaterialInstance.set(newObject.userData.guid, instance);
			instance.object = newObject;
		}

	}

	static registerCustomMaterialReference(referenceObject: THREE.Object3D | EffectorEventInvocation) : void
	{
		if(referenceObject instanceof THREE.Object3D)
		{
			if(referenceObject.userData.visibility?.customDeviceMaterial)
			{
				const materialAsset = MaterialAsset.getMaterialAssetByGuid(referenceObject.userData.visibility.customDeviceMaterial);
				if(materialAsset) materialAsset.customMaterialObjects.add(referenceObject);
			}
			if(referenceObject.userData.visibility?.customStandaloneMaterial)
			{
				const materialAsset = MaterialAsset.getMaterialAssetByGuid(referenceObject.userData.visibility.customStandaloneMaterial);
				if(materialAsset) materialAsset.customMaterialObjects.add(referenceObject);
			}

			if(referenceObject.userData.triggers)
			{
				for(const trigger of referenceObject.userData.triggers)
				{
					if(trigger.customMaterial)
					{
						const materialAsset = MaterialAsset.getMaterialAssetByGuid(trigger.customMaterial);
						if(materialAsset) materialAsset.customMaterialObjects.add(referenceObject);
					}
				}
			}
		}
		else if(referenceObject instanceof EffectorEventInvocation)
		{
			if(referenceObject.customMaterial)
			{
				const materialAsset = MaterialAsset.getMaterialAssetByGuid(referenceObject.customMaterial);
				if(materialAsset) materialAsset.customMaterialObjects.add(referenceObject);
			}
		}
	}

	static replaceObjectCustomMaterialReferences(materialAsset: MaterialAsset, newMaterialAsset: MaterialAsset | undefined = undefined, updateVisibility = false) : void
	{
		for(const object of materialAsset.customMaterialObjects)
		{
			const newMaterialGuid = newMaterialAsset?.guid?? undefined;
			if(object instanceof THREE.Object3D)
			{
				if(object.userData.visibility?.customDeviceMaterial === materialAsset.guid)
				{
					DefaultSceneManager.getInstance().syncVisibilityModeCustomMaterial(object, RenderModes.Device, newMaterialGuid);
					object.userData.visibility.customDeviceMaterial = newMaterialGuid;
					ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Visibility);
					if(updateVisibility) VisibilityManager.setObjectsVisibilityMode([object], [object.userData.visibility[RenderModes.Device]]);
				}
				if(object.userData.visibility?.customStandaloneMaterial === materialAsset.guid)
				{
					DefaultSceneManager.getInstance().syncVisibilityModeCustomMaterial(object, RenderModes.Standalone, newMaterialGuid);
					object.userData.visibility.customStandaloneMaterial = newMaterialGuid;
					ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Visibility);
					if(updateVisibility) VisibilityManager.setObjectsVisibilityMode([object], [object.userData.visibility[RenderModes.Standalone]]);
				}

				if(object.userData.triggers)
				{
					for(let i = 0; i < object.userData.triggers.length; i++)
					{
						const trigger = object.userData.triggers[i];
						if(trigger.customMaterial === materialAsset.guid)
						{
							DefaultSceneManager.getInstance().syncTriggerProperty([object], i, 'customMaterial', [newMaterialGuid ?? defaultTriggerObject.customMaterial]);
							trigger.customMaterial = newMaterialGuid ?? defaultTriggerObject.customMaterial;
							ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Trigger);
						}
					}
				}
			}
			else if(object instanceof EffectorEventInvocation)
			{
				if(object.customMaterial === materialAsset.guid)
				{
					object.customMaterial = newMaterialGuid;
				}
			}
		}
	}

	static mergeIdenticalMaterials() : void
	{
		const materialsToDelete: MaterialAsset[] = [];

		function compareMaterials(a: MaterialAsset, b: MaterialAsset)
		{
			if(a.type !== b.type) return false;
			switch (a.type)
			{
				case MaterialTypes.DiffuseUnlit:
					return (MathUtils.areColorsEqual(a.color, b.color) && a.map === b.map && a._alphaMode === b._alphaMode && a.opacity === b.opacity && a.alphaTest === b.alphaTest);
				case MaterialTypes.Standard:
					return ( MathUtils.areColorsEqual(a.color, b.color) && a.map === b.map && a._alphaMode === b._alphaMode && a.opacity === b.opacity && a.alphaTest === b.alphaTest
						&& a.normalMap === b.normalMap && a.emissiveMap === b.emissiveMap && a.metalness === b.metalness && a.metallicRoughnessMap === b.metallicRoughnessMap
						&& a.roughness === b.roughness && MathUtils.areColorsEqual(a.emissive, b.emissive) && a.aoMap === b.aoMap);
				case MaterialTypes.Video:
					return ( MathUtils.areColorsEqual(a.color, b.color) && a.video === b.video);
				case MaterialTypes.Line:
					return ( MathUtils.areColorsEqual(a.lineColor, b.lineColor) && a.lineThickness === b.lineThickness);
				case MaterialTypes.DepthMask:
					return true;
				default:
					return false;
			}
		}

		function mergeMaterials(a: MaterialAsset, b: MaterialAsset) : void
		{
			const materialToKeep = a.name.localeCompare(b.name) <= 0 ? a : b;
			const materialToDelete = materialToKeep === a ? b : a;

			MaterialAsset.replaceObjectCustomMaterialReferences(materialToDelete, materialToKeep, false);

			for(let i = materialToDelete.assignedObjects.length-1; i >= 0; i--)
			{
				const object = materialToDelete.assignedObjects[i];
				MaterialAsset.assignMaterialAssetToObject(materialToKeep, object, true);
				ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Material);
			}

			const objectsWithMaterialInstance = MaterialAsset.materialInstanceAssetToObjects.get(materialToDelete.guid);
			if(objectsWithMaterialInstance)
			{
				for(const object of objectsWithMaterialInstance)
				{
					MaterialAsset.updateMaterialTimelineInstance(object, materialToKeep.internalMaterial!);
					if (materialToDelete.type === MaterialTypes.Video)
					{
						MaterialAsset.getTimelineMaterialInstance(object)?.assignVideoElementClone();
					}
				}
			}

			materialsToDelete.push(materialToDelete);
		}

		for (const materialAssetIteratorA of MaterialAsset.materialAssetBank.values())
		{
			if(materialAssetIteratorA.guid === MaterialAsset.defaultMaterialGuid) continue;

			for (const materialAssetIteratorB of MaterialAsset.materialAssetBank.values())
			{
				if(materialsToDelete.includes(materialAssetIteratorA)) break;
				if(materialAssetIteratorB.guid === MaterialAsset.defaultMaterialGuid ||
					materialAssetIteratorA.guid === materialAssetIteratorB.guid ||
					materialsToDelete.includes(materialAssetIteratorB)) continue;

				if(compareMaterials(materialAssetIteratorA, materialAssetIteratorB))
				{
					mergeMaterials(materialAssetIteratorA, materialAssetIteratorB);
				}
			}
		}

		for(const material of materialsToDelete)
		{
			MaterialAsset.deleteMaterial(material.guid, false);
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.updateMaterialComponent));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updatePropertiesComponent));
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updateTriggerComponent));
		AssetsLibraryUtilities.refresh();
	}

	static saveMaterialData(referencedAssetsList: Set<string>) : MaterialsSaveData
	{
		const materials : MaterialSaveObject[] = [];
		const textures: string[] = [];
		const videos: string[] = [];

		for (const materialAsset of MaterialAsset.materialAssetBank.values())
		{
			if(materialAsset.guid === MaterialAsset.defaultMaterialGuid || materialAsset.isHiddenFromView()) continue;
			const material: MaterialSaveObject =
			{
				guid: materialAsset.guid,
				type: materialAsset.type,
				name: materialAsset.name,
				alphaMode: materialAsset._alphaMode,
				alphaTest: materialAsset.alphaTest,
				opacity: materialAsset.opacity,
				metalness: materialAsset.metalness,
				roughness: materialAsset.roughness,
				color: '0x' + store.getState().materialsModule.materials[materialAsset.guid].color.value.substr(1),
				emissive: '0x' + store.getState().materialsModule.materials[materialAsset.guid].emissive.value.substr(1),
				lineColor: '0x' + store.getState().materialsModule.materials[materialAsset.guid].lineColor.value.substr(1),
				lineThickness: materialAsset.lineThickness,
			};
			//srgb<->linear conversions are prone to precision errors, so we save material color values in srgb space (as they appear in the materialsModule)
			//this ensures that upon loading the project, the visibile UI color values will match the values input by the user

			function writeTextureData(textureProperty: string)
			{
				if(materialAsset[textureProperty])
				{
					material[textureProperty] = materialAsset[textureProperty].userData.assetId;
					if(!textures.includes(material[textureProperty])) textures.push(material[textureProperty]);
				}
			}
			writeTextureData("map");
			writeTextureData("aoMap");
			writeTextureData("normalMap");
			writeTextureData("emissiveMap");
			writeTextureData("metallicRoughnessMap");

			if(materialAsset.type === MaterialTypes.Video && materialAsset.video)
			{
				material.video = materialAsset.video.userData.assetId;
				if(material.video && !videos.includes(material.video)) videos.push(material.video);
			}

			materials.push(material);
		}

		for(const textureAssetId of textures)
		{
			referencedAssetsList.add(textureAssetId);
		}

		for(const videoAssetId of videos)
		{
			referencedAssetsList.add(videoAssetId);
		}

		return {
			materials: materials,
			textures: textures,
			videos: videos,
		}
	}
}
